(ns common.transaction-process.templates.authoring
  "Customer-agnostic authoring DSL for transaction-process templates — the small
   set of reusable helpers a customer content namespace (e.g. `capman-common`)
   composes its template from. These are pure field-config / rule-fragment builders
   with no customer content baked in: stages, teams, schemas, and the actual field
   dictionaries all live in the per-customer content namespace and are passed in here as
   arguments. This keeps the engine + shared DSL separate from customer content,
   made explicit at the template layer.")

(defn field-visible-when
  "Field-config fragment: conditionally visible based on another field. With one
  arg, visible when that boolean field is truthy. With three args, visible
  when the field matches the given operator+value. Mandatoriness defaults to
  :mandatory/when-visible (callers can override with merge)."
  ([field-kw]
   (field-visible-when field-kw :operand/true nil))
  ([field-kw operator value]
   (let [rule (cond-> {:query-type :query-type/single-item
                       :operator   operator
                       :field      field-kw}
                (some? value) (assoc :value value))]
     {:field/visibility       :visibility/conditional
      :field/visibility-rules [rule]
      :field/mandatoriness    :mandatory/when-visible})))

(defn visible-from-stage
  "Create a visibility rule: visible when the TP's stage is any of the given stages.
   Reads the real `:transaction-process/stage` attribute, so it evaluates correctly
   against any process-derived lookup with no extra wiring."
  [& stages]
  (field-visible-when :transaction-process/stage :operand/any-of (vec stages)))

(defn tx-type-rule
  "Returns the underlying mandatoriness-rule vector matching when
  :transaction-process/transaction-type is one of the given tx-types."
  [tx-types]
  [{:query-type :query-type/single-item
    :operator   :operand/any-of
    :field      :transaction-process/transaction-type
    :value      (vec tx-types)}])

(defn mandatory-when-tx-type
  "Field-shape rule: mandatory when transaction-type is any of the given types.
  Resolved via common.mandatory/calc-mandatoriness."
  [tx-types]
  {:field/mandatoriness       :mandatory/conditional
   :field/mandatoriness-rules (tx-type-rule tx-types)})

(defn transaction-type-options
  "Convert :template/transaction-types entries into :field/options shape."
  [transaction-types]
  (mapv (fn [{:transaction-type/keys [id label]}]
          {:value id :label label})
        transaction-types))

(defn attachment-slot-field
  "Build an attachment-slot template field-config. A document slot is rendered in
   the TP form as `:data.type/attachment-slot`, while its schema field is a
   calculated ref-multi over `:common/attachments`, filtered by each attachment's
   `:common/system-kws` tag (= the field-kw). `opts` can carry `:field/multiple?`
   and a mandatoriness rule (e.g. `(mandatory-when-tx-type ...)`)."
  ([label] (attachment-slot-field label nil))
  ([label opts]
   (merge {:field/label         label
           :field/data-type     :data.type/attachment-slot
           :field/visibility    :visibility/always
           :field/full-width    true
           :field/mandatoriness :mandatory/optional}
          opts)))

(defn enrich-from-schema
  "Merge shared field metadata into a template field config from the given
   `schema-lookup` (a field-kw -> schema-metadata map supplied by the customer
   content namespace). Template-provided values win; schema options are normalized
   from the schema's {:key :label} shape to the template's {:value :label} shape.
   Skips fields not found in the lookup (e.g. sub-entity fields)."
  [{:keys [schema-lookup field-kw field-config]}]
  (if-let [schema-meta (get schema-lookup field-kw)]
    (cond-> field-config
      (not (:field/label field-config))
      (assoc :field/label (:field/name schema-meta))
      (not (:field/data-type field-config))
      (assoc :field/data-type (:field/data-type schema-meta))
      (and (not (:field/options field-config))
           (seq (:options schema-meta)))
      (assoc :field/options (mapv (fn [{:keys [key value label]}]
                                    {:value (or value key) :label label})
                                  (:options schema-meta)))
      (and (not (:field/entity-types field-config))
           (seq (:field/entity-types schema-meta)))
      (assoc :field/entity-types (:field/entity-types schema-meta))
      (and (not (:field/select-entity-types field-config))
           (seq (:field/select-entity-types schema-meta)))
      (assoc :field/select-entity-types (:field/select-entity-types schema-meta))
      (and (not (:field/create-entity-types field-config))
           (seq (:field/create-entity-types schema-meta)))
      (assoc :field/create-entity-types (:field/create-entity-types schema-meta))
      (and (not (:field/option-filter-fn field-config))
           (:field/option-filter-fn schema-meta))
      (assoc :field/option-filter-fn (:field/option-filter-fn schema-meta))
      (and (not (:field/os-filter-terms-fn field-config))
           (:field/os-filter-terms-fn schema-meta))
      (assoc :field/os-filter-terms-fn (:field/os-filter-terms-fn schema-meta)))
    field-config))

(defn enrich-field-configs
  "Enrich every field config in `configs` against `schema-lookup`. `configs` is
   first so this composes in a thread-first field-dictionary pipeline."
  [configs schema-lookup]
  (into {} (map (fn [[field-kw field-config]]
                  [field-kw (enrich-from-schema
                             {:schema-lookup schema-lookup
                              :field-kw      field-kw
                              :field-config  field-config})])
                configs)))
